import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

type CategoryKind = "audit" | "support";

interface TicketCategory {
  id: number;
  name: string;
  name_en: string;
  name_it: string;
  name_pt: string;
  description: string;
  is_active: boolean;
  kind: CategoryKind;
  kind_number: number;
  assignment_count: number;
}

interface FormValues {
  name: string;
  name_en?: string;
  name_it?: string;
  name_pt?: string;
  description?: string;
  is_active: boolean;
  kind: CategoryKind;
  kind_number?: number;
}

/**
 * Admin page for `TicketCategory` — catalogue of categories used to
 * classify tickets/incidencias. Supports the same flow as the
 * business-positions admin:
 *   - CRUD with translations (EN/IT/PT) on each category.
 *   - Delete blocked when the category is referenced by tickets.
 *   - Bulk merge: pick a target, the backend reassigns every
 *     referencing ticket to it and deletes the sources. Only allowed
 *     between categories of the same `kind` (audit vs support).
 */
export const TicketCategoryAdminList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  usePageTitle(t("ticket_categories.title", "Categorías de incidencias"));

  const [data, setData] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  // CRUD modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<TicketCategory | null>(null);
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  // Merge modal
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  // ----- data fetching ----------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Admin endpoint is paginated (Refine-compatible). We pull a
      // big enough page since the catalogue is small.
      const r = await apiClient.get("/v1/admin/ticket-categories/", {
        params: { page_size: 200 },
      });
      const list: TicketCategory[] = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).data ?? (r.data as any).results ?? []);
      setData(list);
    } catch {
      notify.error(
        t("ticket_categories.load_error", "Error al cargar las categorías"),
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ----- CRUD --------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, kind: "support", kind_number: 0 });
    setEditorOpen(true);
  };

  const openEdit = (row: TicketCategory) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      name_en: row.name_en ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
      description: row.description ?? "",
      is_active: row.is_active,
      kind: row.kind,
      kind_number: row.kind_number,
    });
    setEditorOpen(true);
  };

  const submitEditor = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(
          `/v1/admin/ticket-categories/${editing.id}/`,
          values,
        );
        notify.success(
          t("ticket_categories.update_success", "Categoría actualizada"),
        );
      } else {
        await apiClient.post("/v1/admin/ticket-categories/", values);
        notify.success(
          t("ticket_categories.create_success", "Categoría creada"),
        );
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.name?.[0] ??
        t("ticket_categories.save_error", "No se pudo guardar la categoría");
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (row: TicketCategory) => {
    try {
      await apiClient.delete(`/v1/admin/ticket-categories/${row.id}/`);
      notify.success(
        t("ticket_categories.delete_success", "Categoría eliminada"),
      );
      setSelectedRowKeys((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("ticket_categories.delete_error", "No se pudo eliminar la categoría");
      notify.error(msg);
    }
  };

  // ----- merge -------------------------------------------------------

  const selectedRows = useMemo(
    () => data.filter((r) => selectedRowKeys.includes(r.id)),
    [data, selectedRowKeys],
  );

  // Detect if the selection mixes kinds — that's invalid; the backend
  // would reject it. Surface the warning up-front.
  const mixedKinds = useMemo(() => {
    const kinds = new Set(selectedRows.map((r) => r.kind));
    return kinds.size > 1;
  }, [selectedRows]);

  const openMerge = () => {
    if (selectedRowKeys.length < 2) {
      notify.warning(
        t(
          "ticket_categories.merge_need_two",
          "Selecciona al menos dos categorías para fusionar.",
        ),
      );
      return;
    }
    if (mixedKinds) {
      notify.warning(
        t(
          "ticket_categories.merge_mixed_kinds",
          "Solo puedes fusionar categorías del mismo tipo (Auditoría o Soporte).",
        ),
      );
      return;
    }
    const heaviest = [...selectedRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeTargetId(heaviest?.id ?? selectedRowKeys[0]);
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    if (!mergeTargetId) return;
    const sources = selectedRowKeys.filter((k) => k !== mergeTargetId);
    if (sources.length === 0) {
      notify.warning(
        t(
          "ticket_categories.merge_no_sources",
          "Selecciona al menos una categoría distinta del destino.",
        ),
      );
      return;
    }
    setMerging(true);
    try {
      const res = await apiClient.post<{ moved_tickets: number }>(
        "/v1/admin/ticket-categories/merge/",
        { target_id: mergeTargetId, source_ids: sources },
      );
      notify.success(
        t(
          "ticket_categories.merge_success",
          "Fusionadas {{n}} categorías · {{moved}} tickets reasignados",
          {
            n: sources.length,
            moved: res.data?.moved_tickets ?? 0,
          },
        ),
      );
      setMergeOpen(false);
      setSelectedRowKeys([]);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("ticket_categories.merge_error", "No se pudo fusionar");
      notify.error(msg);
    } finally {
      setMerging(false);
    }
  };

  // ----- table -------------------------------------------------------

  const columns = [
    {
      title: t("ticket_categories.name", "Nombre"),
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: false } as any,
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <strong>{v}</strong>
        </Tooltip>
      ),
      sorter: (a: TicketCategory, b: TicketCategory) =>
        a.name.localeCompare(b.name),
    },
    {
      title: t("ticket_categories.kind", "Tipo"),
      dataIndex: "kind",
      key: "kind",
      width: 120,
      render: (v: CategoryKind) =>
        v === "audit" ? (
          <Tag color="orange">{t("ticket_categories.kind_audit", "Auditoría")}</Tag>
        ) : (
          <Tag color="blue">{t("ticket_categories.kind_support", "Soporte")}</Tag>
        ),
      filters: [
        { text: t("ticket_categories.kind_audit", "Auditoría"), value: "audit" },
        { text: t("ticket_categories.kind_support", "Soporte"), value: "support" },
      ],
      onFilter: (value: any, row: TicketCategory) => row.kind === value,
    },
    {
      title: t("ticket_categories.translations", "Traducciones"),
      key: "translations",
      width: 220,
      render: (_: any, row: TicketCategory) => {
        const entries = [
          { code: "EN", label: "English", value: row.name_en },
          { code: "IT", label: "Italiano", value: row.name_it },
          { code: "PT", label: "Português", value: row.name_pt },
        ];
        return (
          <Space size={4} wrap>
            {entries.map((e) =>
              e.value ? (
                <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
                  <Tag color="blue" style={{ margin: 0 }}>{e.code}</Tag>
                </Tooltip>
              ) : (
                <Tooltip
                  key={e.code}
                  title={`${e.label}: ${t("ticket_categories.no_translation", "sin traducir")}`}
                >
                  <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ),
            )}
          </Space>
        );
      },
    },
    {
      title: t("ticket_categories.assignment_count", "Tickets"),
      dataIndex: "assignment_count",
      key: "assignment_count",
      width: 110,
      align: "center" as const,
      sorter: (a: TicketCategory, b: TicketCategory) =>
        a.assignment_count - b.assignment_count,
      render: (n: number) =>
        n > 0 ? (
          <Tag color="blue">{n}</Tag>
        ) : (
          <Tag color="default">0</Tag>
        ),
    },
    {
      title: t("ticket_categories.active", "Activa"),
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center" as const,
      render: (v: boolean) =>
        v ? (
          <Tag color="green">{t("common.yes", "Sí")}</Tag>
        ) : (
          <Tag>{t("common.no", "No")}</Tag>
        ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 130,
      align: "right" as const,
      render: (_: any, row: TicketCategory) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t(
              "ticket_categories.delete_confirm",
              "¿Eliminar esta categoría?",
            )}
            description={
              row.assignment_count > 0
                ? t(
                    "ticket_categories.delete_blocked",
                    "No puedes eliminar una categoría con tickets asignados. Fusiónala primero.",
                  )
                : t(
                    "ticket_categories.delete_warning",
                    "Esta acción no se puede deshacer.",
                  )
            }
            onConfirm={() => deleteCategory(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true }}
            disabled={row.assignment_count > 0}
          >
            <Tooltip
              title={
                row.assignment_count > 0
                  ? t(
                      "ticket_categories.delete_blocked_tooltip",
                      "Tiene tickets asignados — no se puede eliminar",
                    )
                  : t("common.delete", "Eliminar")
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={row.assignment_count > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<AppstoreOutlined />}
        accent="#722ED1"
        title={t("ticket_categories.title", "Categorías de incidencias")}
        subtitle={t(
          "ticket_categories.subtitle",
          "Catálogo de categorías de incidencias/tickets. Fusiona los duplicados para mantener la lista limpia.",
        )}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Tooltip
              title={
                selectedRowKeys.length < 2
                  ? t(
                      "ticket_categories.merge_hint_tooltip",
                      "Marca las casillas de 2 o más categorías para poder fusionarlas",
                    )
                  : mixedKinds
                    ? t(
                        "ticket_categories.merge_mixed_kinds_tooltip",
                        "Has marcado categorías de tipos distintos — selecciona solo Auditoría o solo Soporte",
                      )
                    : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openMerge}
                type={
                  selectedRowKeys.length >= 2 && !mixedKinds
                    ? "primary"
                    : "default"
                }
              >
                {t("ticket_categories.merge", "Fusionar")}
                {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("ticket_categories.create", "Nueva categoría")}
            </Button>
          </Space>
        }
      />

      {selectedRowKeys.length < 2 && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 12 }}
          message={t(
            "ticket_categories.merge_banner",
            "Cómo fusionar: marca las casillas (☐) de las categorías duplicadas del mismo tipo y pulsa Fusionar. Podrás elegir cuál mantener y los tickets del resto se reasignarán automáticamente.",
          )}
        />
      )}
      {selectedRowKeys.length >= 2 && mixedKinds && (
        <Alert
          showIcon
          type="warning"
          style={{ marginBottom: 12 }}
          message={t(
            "ticket_categories.merge_mixed_kinds_banner",
            "Has marcado categorías de tipos distintos (Auditoría y Soporte). La fusión solo funciona dentro del mismo tipo — deselecciona las que sobren.",
          )}
        />
      )}

      <Table<TicketCategory>
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1080 }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          columnWidth: 48,
          fixed: "left",
        }}
      />

      {/* Create / Edit modal */}
      <Modal
        title={
          editing
            ? t("ticket_categories.edit_title", "Editar categoría")
            : t("ticket_categories.create_title", "Nueva categoría")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            label={t("ticket_categories.kind", "Tipo")}
            name="kind"
            rules={[{ required: true }]}
            extra={t(
              "ticket_categories.kind_hint",
              "Auditoría = categorías de incidencias de auditoría. Soporte = tickets internos.",
            )}
          >
            <Select
              // Render the dropdown inside the modal so it inherits
              // the Modal's stacking context and never gets covered
              // by it. The explicit z-index is a belt-and-braces
              // guard in case the modal lives in a separate portal.
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
              dropdownStyle={{ zIndex: 10000 }}
              options={[
                {
                  value: "audit",
                  label: t("ticket_categories.kind_audit", "Auditoría"),
                },
                {
                  value: "support",
                  label: t("ticket_categories.kind_support", "Soporte"),
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={t("ticket_categories.name_es", "Nombre (Español)")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "ticket_categories.name_required",
                  "El nombre es obligatorio",
                ),
              },
              { max: 200 },
            ]}
            extra={t(
              "ticket_categories.name_es_hint",
              "Idioma principal del catálogo — siempre obligatorio.",
            )}
          >
            <Input
              maxLength={200}
              placeholder={t(
                "ticket_categories.name_placeholder",
                "Ej: Datos incorrectos",
              )}
              autoFocus
            />
          </Form.Item>

          <Text
            type="secondary"
            style={{ fontSize: 12, display: "block", marginTop: 4, marginBottom: 8 }}
          >
            {t(
              "ticket_categories.translations_hint",
              "Traducciones opcionales — se usan en los exports y la UI según el idioma del usuario.",
            )}
          </Text>

          <Form.Item
            label={t("ticket_categories.name_en", "Nombre (English)")}
            name="name_en"
            rules={[{ max: 200 }]}
          >
            <Input
              maxLength={200}
              placeholder={t(
                "ticket_categories.name_en_placeholder",
                "e.g. Incorrect data",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("ticket_categories.name_it", "Nome (Italiano)")}
            name="name_it"
            rules={[{ max: 200 }]}
          >
            <Input
              maxLength={200}
              placeholder={t(
                "ticket_categories.name_it_placeholder",
                "es. Dati errati",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("ticket_categories.name_pt", "Nome (Português)")}
            name="name_pt"
            rules={[{ max: 200 }]}
          >
            <Input
              maxLength={200}
              placeholder={t(
                "ticket_categories.name_pt_placeholder",
                "ex: Dados incorretos",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("ticket_categories.description", "Descripción")}
            name="description"
            rules={[{ max: 500 }]}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder={t(
                "ticket_categories.description_placeholder",
                "Descripción opcional",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("ticket_categories.active", "Activa")}
            name="is_active"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t("common.yes", "Sí")}
              unCheckedChildren={t("common.no", "No")}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Merge modal */}
      <Modal
        title={t("ticket_categories.merge_title", "Fusionar categorías")}
        open={mergeOpen}
        onCancel={() => (merging ? undefined : setMergeOpen(false))}
        onOk={submitMerge}
        confirmLoading={merging}
        okText={t("ticket_categories.merge_apply", "Fusionar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        okButtonProps={{ danger: true }}
        width={520}
      >
        <Text type="secondary">
          {t(
            "ticket_categories.merge_hint",
            "Selecciona la categoría que quieres mantener. Las demás se eliminarán y todos sus tickets se reasignarán a la elegida.",
          )}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Radio.Group
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          >
            <Space direction="vertical">
              {selectedRows.map((row) => (
                <Radio key={row.id} value={row.id}>
                  <strong>{row.name}</strong>{" "}
                  <Text type="secondary">
                    ({row.assignment_count}{" "}
                    {row.assignment_count === 1
                      ? t("ticket_categories.ticket_one", "ticket")
                      : t("ticket_categories.ticket_many", "tickets")})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default TicketCategoryAdminList;
